import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api/api";
import { useListSecrets } from "@/lib/hooks/useSecrets";
import { AlertCircle, KeyRound, Loader2, Trash } from "lucide-react";
import { useEffect, useState } from "react";

interface EnvVarPopoverProps {
  chatId: string;
}

export function EnvVarPopover({ chatId }: EnvVarPopoverProps) {
  const { toast } = useToast();

  // Centralized toast notification helper
  const showToast = {
    secretAdded: (key: string) => {
      toast({
        title: "Secret added",
        description: `"${key}" has been saved successfully`,
        duration: 2000,
      });
    },
    secretDeleted: (key: string) => {
      toast({
        title: "Secret deleted",
        description: `"${key}" has been removed successfully`,
        duration: 2000,
      });
    },
    error: (title: string, description: string) => {
      toast({
        variant: "destructive",
        title,
        description,
        duration: 5000,
      });
    },
  };

  // Use API integration for loading existing secrets
  const { data: secretsResponse, isLoading, error } = useListSecrets(chatId);
  const existingSecretKeys = secretsResponse?.data.secretKeys || [];

  // Local state for new variables being added (for immediate UI feedback)
  const [vars, setVars] = useState<Array<{ key: string; value: string }>>([]);
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const [isAddingSecret, setIsAddingSecret] = useState(false);
  const [operationError, setOperationError] = useState<string | null>(null);
  const [deletingSecrets, setDeletingSecrets] = useState<Set<string>>(
    new Set(),
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [secretToDelete, setSecretToDelete] = useState<string | null>(null);

  // Clean up local state when secrets appear in API response
  useEffect(() => {
    if (existingSecretKeys.length > 0) {
      setVars((prevVars) =>
        prevVars.filter(
          (localVar) => !existingSecretKeys.includes(localVar.key),
        ),
      );
    }
  }, [existingSecretKeys]);

  // Validation function for secret keys
  const validateSecretKey = (secretKey: string): string | null => {
    if (!secretKey.trim()) {
      return "Secret key is required";
    }
    if (!/^[A-Z_][A-Z0-9_]*$/i.test(secretKey)) {
      return "Secret key must start with a letter or underscore and contain only letters, numbers, and underscores";
    }
    if (existingSecretKeys.includes(secretKey)) {
      return "Secret key already exists";
    }
    if (vars.some((v) => v.key === secretKey)) {
      return "Secret key already added";
    }
    return null;
  };

  const validateSecretValue = (secretValue: string): string | null => {
    if (!secretValue.trim()) {
      return "Secret value is required";
    }
    return null;
  };

  const keyError = key ? validateSecretKey(key) : null;
  const valueError = value ? validateSecretValue(value) : null;
  const isFormValid = !keyError && !valueError && key.trim() && value.trim();

  // Add secret via CHAT WebSocket (mutations happen immediately)
  async function add() {
    if (!isFormValid) {
      return;
    }

    setIsAddingSecret(true);
    setOperationError(null);

    try {
      // Send secret mutation via CHAT WebSocket
      const success = await api.setSecret(chatId, key.trim(), value.trim());

      if (success) {
        // Add to local state for immediate UI feedback
        setVars([...vars, { key: key.trim(), value: value.trim() }]);
        setKey("");
        setValue("");

        // Show success toast
        showToast.secretAdded(key.trim());
      } else {
        setOperationError(
          "Failed to add secret. Please check your connection and try again.",
        );

        // Show error toast
        showToast.error(
          "Failed to add secret",
          "Please check your connection and try again.",
        );
      }
    } catch (error) {
      console.error("Error adding secret:", error);
      setOperationError(
        "An error occurred while adding the secret. Please try again.",
      );

      // Show error toast
      showToast.error(
        "Error adding secret",
        "An error occurred while adding the secret. Please try again.",
      );
    } finally {
      setIsAddingSecret(false);
    }
  }

  // Remove new variable from local state
  function remove(index: number) {
    setVars(vars.filter((_, i) => i !== index));
  }

  // Remove existing secret via CHAT WebSocket
  async function removeExistingSecret(secretKey: string) {
    setDeletingSecrets((prev) => new Set(prev).add(secretKey));
    setOperationError(null);

    try {
      const success = await api.deleteSecret(chatId, secretKey);

      if (success) {
        // Show success toast
        showToast.secretDeleted(secretKey);
      } else {
        setOperationError(
          `Failed to delete secret "${secretKey}". Please try again.`,
        );

        // Show error toast
        showToast.error(
          "Failed to delete secret",
          `Could not delete "${secretKey}". Please try again.`,
        );
      }
      // Note: Real-time updates via WebSocket will refresh the UI automatically
    } catch (error) {
      console.error("Error deleting secret:", error);
      setOperationError(
        `An error occurred while deleting secret "${secretKey}". Please try again.`,
      );

      // Show error toast
      showToast.error(
        "Error deleting secret",
        `An error occurred while deleting "${secretKey}". Please try again.`,
      );
    } finally {
      setDeletingSecrets((prev) => {
        const newSet = new Set(prev);
        newSet.delete(secretKey);
        return newSet;
      });
    }
  }

  // Clear operation error when user starts typing
  const handleKeyChange = (newKey: string) => {
    setKey(newKey);
    if (operationError) {
      setOperationError(null);
    }
  };

  const handleValueChange = (newValue: string) => {
    setValue(newValue);
    if (operationError) {
      setOperationError(null);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          size="icon"
          className="h-7 w-7"
          aria-label="Add Environment Variable"
        >
          <KeyRound className="w-4 h-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[396px] p-0 border-0"
        align="end"
        side="bottom"
      >
        <div className="">
          <div className="px-1">
            <h4 className="h-8 p-2 font-4 leading-5">
              Add Environment Variables
            </h4>
          </div>
          <hr className="my-1" />
          <div className="py-3">
            <div className="flex flex-col gap-4 px-3">
              <p className="text-xs leading-4">
                Environment variables will be encrypted and stored securely
                on...
              </p>

              {/* Error display */}
              {(error || operationError) && (
                <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-red-700">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span className="text-xs">
                    {operationError ||
                      (error instanceof Error
                        ? error.message
                        : "Failed to load secrets")}
                  </span>
                </div>
              )}

              {isLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="ml-2 text-sm text-muted-foreground">
                    Loading secrets...
                  </span>
                </div>
              ) : (
                <>
                  {/* Display existing secrets (keys only, no values for security) */}
                  {existingSecretKeys.map((secretKey) => {
                    const isDeleting = deletingSecrets.has(secretKey);
                    return (
                      <div key={`existing-${secretKey}`} className="flex gap-2">
                        <div className="flex gap-2 flex-1">
                          <div className="w-1/2">
                            <Input
                              placeholder="API_KEY"
                              value={secretKey}
                              readOnly
                              className="h-8 py-2 px-3 bg-muted"
                            />
                          </div>
                          <div className="w-1/2">
                            <Input
                              placeholder="[Hidden]"
                              value="••••••••"
                              readOnly
                              className="h-8 py-2 px-3 bg-muted"
                            />
                          </div>
                        </div>
                        <div className="w-12 flex justify-center">
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-8 py-2 px-3"
                            onClick={() => {
                              setSecretToDelete(secretKey);
                              setDeleteDialogOpen(true);
                            }}
                            aria-label="Delete secret"
                            disabled={isDeleting}
                          >
                            {isDeleting ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })}

                  {/* Display new variables being added (for immediate UI feedback) */}
                  {vars.map((v, i) => (
                    <div key={`new-${v.key}-${i}`} className="flex gap-2">
                      <div className="flex gap-2 flex-1">
                        <div className="w-1/2">
                          <Input
                            placeholder="API_KEY"
                            value={v.key}
                            onChange={(e) => {
                              const newVars = [...vars];
                              newVars[i].key = e.target.value;
                              setVars(newVars);
                            }}
                            className="h-8 py-2 px-3"
                          />
                        </div>
                        <div className="w-1/2">
                          <Input
                            placeholder="my-api-key"
                            value={v.value}
                            onChange={(e) => {
                              const newVars = [...vars];
                              newVars[i].value = e.target.value;
                              setVars(newVars);
                            }}
                            className="h-8 py-2 px-3"
                          />
                        </div>
                      </div>
                      <div className="w-12 flex justify-center">
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-8 py-2 px-3"
                          onClick={() => remove(i)}
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Input for adding new variables */}
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <div className="flex gap-2 flex-1">
                    <div className="w-1/2">
                      <Input
                        placeholder="API_KEY"
                        value={key}
                        onChange={(e) => handleKeyChange(e.target.value)}
                        className={`h-8 py-2 px-3 ${keyError ? "border-red-500" : ""}`}
                        disabled={isLoading || isAddingSecret}
                      />
                    </div>
                    <div className="w-1/2">
                      <Input
                        placeholder="my-api-key"
                        value={value}
                        onChange={(e) => handleValueChange(e.target.value)}
                        className={`h-8 py-2 px-3 ${valueError ? "border-red-500" : ""}`}
                        disabled={isLoading || isAddingSecret}
                      />
                    </div>
                  </div>
                  <div className="w-12 flex justify-center">
                    {/* Empty space to maintain alignment with other rows */}
                    <div className="h-8 w-10 py-2 px-3" aria-hidden="true" />
                  </div>
                </div>

                {/* Validation errors */}
                {(keyError || valueError) && (
                  <div className="text-xs text-red-600 px-1">
                    {keyError || valueError}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="pt-0 p-3">
            <Button
              type="button"
              onClick={add}
              variant="secondary"
              className="inline-flex h-7 py-0 px-3"
              disabled={!isFormValid || isLoading || isAddingSecret}
            >
              {isAddingSecret ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  Adding...
                </>
              ) : (
                "+ Add Variable"
              )}
            </Button>
          </div>

          {/* Save button removed - mutations happen immediately via WebSocket */}
        </div>
      </PopoverContent>
      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) {
            setSecretToDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Secret</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the secret "{secretToDelete}"?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setDeleteDialogOpen(false);
                setSecretToDelete(null);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (secretToDelete) {
                  removeExistingSecret(secretToDelete);
                }
                setDeleteDialogOpen(false);
                setSecretToDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Popover>
  );
}
